import appConfig, {AppConfigStore} from "./app-config.js";
import account, {AccountStore} from "./account.js";
import project, {ProjectStore} from "./project.js";
import topic, {TopicStore} from "./topic.js";
import outline, {OutlineStore} from "./outline.js";
import annotation, {AnnotationStore} from "./annotation.js";
import notification, {NotificationStore} from "./notification.js";
import {outSyncHandler, Sync} from "../sync/index.js";

/**
 * Stores should adhere to certain conventions when naming methods:
 *
 * - `create`, `update`, and `delete` should modify local data, return, and then call the backend asynchronously.
 *   These should accept an optional context object (`ctx`: any) to be used if the backend calls go wrong, in which
 *   case they should revert local changes and call `app.backendErrorHandler()`.
 *
 * - `_load` and `_remove` are used only to modify the local data; they shouldn't call any backend methods, and are not
 *   meant to be called from the outside.
 *
 * - `_fetch` methods are internal and call the backend to load data if it's not already loaded (they should have a
 *   `force` optional argument to override this). They should check `app.isLoggedIn` before calling the backend.
 *
 * - `_xFromSync` should only modify local data.
 */
export interface Store {
	/**
	 * Clears all local data (should happen synchronously).
	 */
	_clear(): void;

	/**
	 * Refreshes all the data that's loaded (should trigger async operations but return immediately).
	 */
	_refresh(): void;

	_setupSyncHandlers(sync: Sync): void;
	_setupOutSyncHandlers(handler: outSyncHandler): void;
}

export default {
	appConfig,
	account,
	project,
	topic,
	outline,
	annotation,
	notification
} as {
	appConfig:    AppConfigStore;
	account:      AccountStore;
	project:      ProjectStore;
	topic:        TopicStore;
	outline:      OutlineStore;
	annotation:   AnnotationStore;
	notification: NotificationStore;
};
